import ExcelJS from "exceljs";
import apiDefault from "./api";

export const normalize = (s) =>
    String(s ?? "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, " ");

export function parseAplicacoesCSV(raw, aplicacoes) {
    const s = String(raw ?? "");
    // Quebra preferencial por nova linha (separador canônico do export atual).
    // Mantém fallback por vírgula para templates legados onde o nome da
    // aplicação não contém vírgulas.
    const separator = /\r?\n/.test(s) ? /\r?\n/ : /,/;
    const parts = s
        .split(separator)
        .map((p) => p.trim())
        .filter(Boolean);

    const matched = [];
    const unknown = [];
    const seenIds = new Set();

    for (const part of parts) {
        const target = normalize(part);
        const found = aplicacoes.find((a) => normalize(a.Aplicacao) === target);
        if (found) {
            if (!seenIds.has(found.id)) {
                seenIds.add(found.id);
                matched.push({ id: found.id });
            }
        } else if (!unknown.includes(part)) {
            unknown.push(part);
        }
    }

    return { matched, unknown };
}

export function findProductByName(nome, produtos) {
    const target = normalize(nome);
    if (!target) return null;
    return produtos.find((p) => normalize(p.Titulo) === target) || null;
}

export function findLinhaByName(nome, linhas) {
    const target = normalize(nome);
    if (!target) return null;
    return linhas.find((l) => normalize(l.Linha) === target) || null;
}

function countByTitulo(produtos, target) {
    return produtos.filter((p) => normalize(p.Titulo) === target).length;
}

export function buildPlan(rawRows, produtos, linhas, aplicacoes) {
    const plan = [];
    const createdInPlanByName = new Map();

    for (const row of rawRows) {
        const nome = String(row.Nome ?? "").trim();
        const linhaRaw = String(row.Linha ?? "").trim();
        const aplicacoesRaw = String(row.Aplicacoes ?? "").trim();
        const normalizedName = normalize(nome);

        if (!normalizedName) {
            plan.push({
                rowNumber: row.rowNumber,
                action: "skip-empty-name",
                payload: null,
                warnings: [],
                matchedProductId: null,
            });
            continue;
        }

        const warnings = [];

        let linhaId = null;
        if (linhaRaw) {
            const found = findLinhaByName(linhaRaw, linhas);
            if (found) {
                linhaId = found.id;
            } else {
                warnings.push(
                    `Linha '${linhaRaw}' não encontrada — produto ficará sem linha`
                );
            }
        }

        const { matched, unknown } = parseAplicacoesCSV(
            aplicacoesRaw,
            aplicacoes
        );
        const aplicacaoIds = matched.map((m) => m.id);
        if (unknown.length > 0) {
            warnings.push(
                `Aplicações não encontradas: ${unknown.join(", ")}`
            );
        }

        if (createdInPlanByName.has(normalizedName)) {
            plan.push({
                rowNumber: row.rowNumber,
                action: "update-pending",
                pendingCreateRow: createdInPlanByName.get(normalizedName),
                payload: { linha: linhaId, aplicacaos: aplicacaoIds },
                warnings: [
                    ...warnings,
                    "Nome duplicado dentro da planilha — será aplicado após a criação anterior",
                ],
                matchedProductId: null,
            });
            continue;
        }

        const existing = findProductByName(nome, produtos);
        if (existing) {
            const dupCount = countByTitulo(produtos, normalizedName);
            if (dupCount > 1) {
                warnings.push(
                    `${dupCount} produtos com este nome — apenas o primeiro (ID ${existing.id}) será atualizado`
                );
            }
            plan.push({
                rowNumber: row.rowNumber,
                action: "update",
                payload: { linha: linhaId, aplicacaos: aplicacaoIds },
                warnings,
                matchedProductId: existing.id,
            });
        } else {
            createdInPlanByName.set(normalizedName, row.rowNumber);
            plan.push({
                rowNumber: row.rowNumber,
                action: "create",
                payload: {
                    Titulo: nome,
                    linha: linhaId,
                    aplicacaos: aplicacaoIds,
                    exclusivo: false,
                },
                warnings,
                matchedProductId: null,
            });
        }
    }

    return plan;
}

export async function applyPlan(
    plan,
    onProgress = () => {},
    api = apiDefault,
    shouldAbort = () => false
) {
    const results = [];
    const createdRowToId = new Map(); // rowNumber → id criado

    let done = 0;

    for (const item of plan) {
        if (shouldAbort()) {
            results.push({
                rowNumber: item.rowNumber,
                status: "aborted",
                message: "Cancelado pelo usuário",
                warnings: item.warnings,
                matchedProductId: null,
            });
            continue;
        }

        try {
            if (item.action === "skip-empty-name") {
                results.push({
                    rowNumber: item.rowNumber,
                    status: "skipped",
                    message: "Linha sem nome",
                    warnings: item.warnings,
                    matchedProductId: null,
                });
            } else if (item.action === "create") {
                const response = await api.post("/produtos", item.payload);
                createdRowToId.set(item.rowNumber, response.data.id);
                results.push({
                    rowNumber: item.rowNumber,
                    status: "created",
                    message: `Criado ID ${response.data.id}`,
                    warnings: item.warnings,
                    matchedProductId: response.data.id,
                });
            } else if (item.action === "update") {
                await api.put(
                    `/produtos/${item.matchedProductId}`,
                    item.payload
                );
                results.push({
                    rowNumber: item.rowNumber,
                    status: "updated",
                    message: `Atualizado ID ${item.matchedProductId}`,
                    warnings: item.warnings,
                    matchedProductId: item.matchedProductId,
                });
            } else if (item.action === "update-pending") {
                const targetId = createdRowToId.get(item.pendingCreateRow);
                if (!targetId) {
                    throw new Error(
                        `Criação da linha ${item.pendingCreateRow} não disponível para esta atualização`
                    );
                }
                await api.put(`/produtos/${targetId}`, item.payload);
                results.push({
                    rowNumber: item.rowNumber,
                    status: "updated",
                    message: `Atualizado ID ${targetId} (criado nesta importação)`,
                    warnings: item.warnings,
                    matchedProductId: targetId,
                });
            } else {
                results.push({
                    rowNumber: item.rowNumber,
                    status: "failed",
                    message: `Ação desconhecida: ${item.action}`,
                    warnings: item.warnings,
                    matchedProductId: null,
                });
            }
        } catch (err) {
            const message =
                (err && err.message) || "Falha ao aplicar linha";
            results.push({
                rowNumber: item.rowNumber,
                status: "failed",
                message,
                warnings: item.warnings,
                matchedProductId: item.matchedProductId ?? null,
            });
        } finally {
            done += 1;
            onProgress({ done, total: plan.length });
        }
    }

    return results;
}

export const HEADER_ERRORS = {
    MISSING_NOME: "Coluna obrigatória 'Nome' não encontrada no cabeçalho",
};

const HEADER_ALIASES = {
    NOME: "Nome",
    TITULO: "Nome",
    LINHA: "Linha",
    APLICACOES: "Aplicacoes",
    "APLICAÇÕES": "Aplicacoes",
    "APLICAÇOES": "Aplicacoes",
    "APLICACÕES": "Aplicacoes",
};

export function mapHeaderRow(headers) {
    const map = {};
    headers.forEach((header, idx) => {
        const key = normalize(header);
        const canonical = HEADER_ALIASES[key];
        if (canonical && !map[canonical]) {
            map[canonical] = idx + 1; // exceljs é 1-based
        }
    });

    if (!map.Nome) {
        throw new Error(HEADER_ERRORS.MISSING_NOME);
    }

    const warnings = [];
    if (!map.Linha) {
        warnings.push("Coluna 'Linha' ausente — campo será ignorado");
    }
    if (!map.Aplicacoes) {
        warnings.push("Coluna 'Aplicações' ausente — campo será ignorado");
    }

    return { map, warnings };
}

export const SHEET_NAME = "v1";

export async function parseSheet(file) {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    let worksheet = workbook.getWorksheet(SHEET_NAME);
    const warnings = [];
    if (!worksheet) {
        worksheet = workbook.worksheets[0];
        if (!worksheet) {
            throw new Error("Planilha sem dados");
        }
        warnings.push(
            `Aba '${SHEET_NAME}' não encontrada — usando '${worksheet.name}'`
        );
    }

    const headerRow = worksheet.getRow(1);
    const headers = [];
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        headers[colNumber - 1] = cell.value;
    });

    if (headers.filter(Boolean).length === 0) {
        throw new Error("Planilha sem cabeçalho");
    }

    const { map, warnings: headerWarnings } = mapHeaderRow(headers);
    warnings.push(...headerWarnings);

    const rows = [];
    const lastRow = worksheet.actualRowCount || worksheet.rowCount;
    for (let i = 2; i <= lastRow; i += 1) {
        const row = worksheet.getRow(i);
        const get = (key) => {
            const col = map[key];
            if (!col) return "";
            const value = row.getCell(col).value;
            if (value === null || value === undefined) return "";
            if (typeof value === "object" && "text" in value) {
                return value.text;
            }
            return String(value);
        };
        rows.push({
            rowNumber: i,
            Nome: get("Nome"),
            Linha: get("Linha"),
            Aplicacoes: get("Aplicacoes"),
        });
    }

    return { rows, warnings };
}

const HEADER_FILL = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
};

const COLUMNS_DEF = [
    { header: "Nome", key: "Nome", width: 32 },
    { header: "Linha", key: "Linha", width: 24 },
    {
        header: "Aplicações",
        key: "Aplicacoes",
        width: 60,
        style: { alignment: { wrapText: true, vertical: "top" } },
    },
];

function applyHeaderStyle(worksheet) {
    const header = worksheet.getRow(1);
    header.font = { bold: true };
    header.eachCell((cell) => {
        cell.fill = HEADER_FILL;
    });
    worksheet.views = [{ state: "frozen", ySplit: 1 }];
}

function productToSheetRow(produto) {
    const linha = (produto.linha && produto.linha.Linha) || "";
    // Uma aplicação por linha dentro da célula: round-trip seguro mesmo
    // quando o nome contém vírgulas (ex.: "LIMPADORES DE PISOS, PAREDES E
    // BANHEIROS"). Excel mostra como multilinha com "wrap text" ligado.
    const aplicacoes = Array.isArray(produto.aplicacaos)
        ? produto.aplicacaos
              .map((a) => a.Aplicacao)
              .filter(Boolean)
              .join("\n")
        : "";
    return { Nome: produto.Titulo || "", Linha: linha, Aplicacoes: aplicacoes };
}

export async function exportProductsToXlsx(produtos, filename) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(SHEET_NAME);
    worksheet.columns = COLUMNS_DEF;
    produtos.forEach((p) => worksheet.addRow(productToSheetRow(p)));
    applyHeaderStyle(worksheet);
    const buffer = await workbook.xlsx.writeBuffer();
    triggerDownload(buffer, filename);
}

export async function downloadTemplate() {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(SHEET_NAME);
    worksheet.columns = COLUMNS_DEF;
    worksheet.addRow({
        Nome: "Exemplo Produto A",
        Linha: "Industrial",
        Aplicacoes: "Solvente\nPintura",
    });
    worksheet.addRow({
        Nome: "Exemplo Produto B",
        Linha: "",
        Aplicacoes: "",
    });
    applyHeaderStyle(worksheet);
    const buffer = await workbook.xlsx.writeBuffer();
    triggerDownload(buffer, "template-produtos.xlsx");
}

export function buildReportRows(rawRows, results) {
    const byRow = new Map(results.map((r) => [r.rowNumber, r]));
    return rawRows.map((row) => {
        const result = byRow.get(row.rowNumber);
        if (!result) {
            return {
                Nome: row.Nome,
                Linha: row.Linha,
                Aplicacoes: row.Aplicacoes,
                Status: "unknown",
                Mensagem: "",
            };
        }
        const parts = [result.message];
        if (result.warnings && result.warnings.length > 0) {
            parts.push(...result.warnings);
        }
        return {
            Nome: row.Nome,
            Linha: row.Linha,
            Aplicacoes: row.Aplicacoes,
            Status: result.status,
            Mensagem: parts.filter(Boolean).join(" | "),
        };
    });
}

export async function downloadReport(rawRows, results, filename) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("relatorio");
    worksheet.columns = [
        { header: "Nome", key: "Nome", width: 32 },
        { header: "Linha", key: "Linha", width: 24 },
        {
            header: "Aplicações",
            key: "Aplicacoes",
            width: 60,
            style: { alignment: { wrapText: true, vertical: "top" } },
        },
        { header: "Status", key: "Status", width: 14 },
        {
            header: "Mensagem",
            key: "Mensagem",
            width: 80,
            style: { alignment: { wrapText: true, vertical: "top" } },
        },
    ];
    buildReportRows(rawRows, results).forEach((r) => worksheet.addRow(r));
    applyHeaderStyle(worksheet);
    const buffer = await workbook.xlsx.writeBuffer();
    triggerDownload(buffer, filename);
}

const XLSX_MIME =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export function triggerDownload(buffer, filename) {
    const blob = new Blob([buffer], { type: XLSX_MIME });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
